import { useEffect, useState } from "react";
import { AppLayout } from "@/components/layout";
import { useChat } from "@/hooks/use-chat";
import { useAuth } from "@/hooks/use-auth";
import {
  Chat,
  ChannelList,
  Channel,
  Window,
  MessageList,
  MessageInput,
  Thread,
  ChannelHeader,
} from "stream-chat-react";
import "stream-chat-react/dist/css/v2/index.css";
import { MessageSquare, Loader2, WifiOff } from "lucide-react";

export default function ChatPage() {
  const { client, isReady, isConfigured } = useChat();
  const { user } = useAuth();

  if (!isConfigured) {
    return (
      <AppLayout>
        <div className="flex flex-col items-center justify-center min-h-[60vh] gap-4">
          <WifiOff className="w-16 h-16 text-muted-foreground opacity-50" />
          <h2 className="text-2xl font-display font-bold text-white">Chat Not Available</h2>
          <p className="text-muted-foreground text-center max-w-md">
            The messaging service is not configured yet. Please contact your administrator.
          </p>
        </div>
      </AppLayout>
    );
  }

  if (!isReady || !client) {
    return (
      <AppLayout>
        <div className="flex flex-col items-center justify-center min-h-[60vh] gap-4">
          <Loader2 className="w-10 h-10 text-primary animate-spin" />
          <p className="text-muted-foreground">Connecting to chat...</p>
        </div>
      </AppLayout>
    );
  }

  const filters = { members: { $in: [user!.id] }, type: "messaging" as const };
  const sort = { last_message_at: -1 as const };

  return (
    <AppLayout>
      <div className="flex flex-col gap-4 pb-4">
        <div>
          <h1 className="text-4xl font-display font-bold text-white mb-2">Messages</h1>
          <p className="text-muted-foreground text-lg">Chat with your trainers and students</p>
        </div>

        <div className="stream-chat-container rounded-2xl overflow-hidden border border-white/10 bg-card" style={{ height: "calc(100vh - 280px)" }}>
          <Chat client={client} theme="str-chat__theme-dark">
            <div className="flex h-full">
              <div className="w-80 border-r border-white/10 shrink-0 overflow-hidden">
                <ChannelList
                  filters={filters}
                  sort={sort}
                  showChannelSearch
                  EmptyStateIndicator={() => (
                    <div className="flex flex-col items-center justify-center h-full p-6 text-center">
                      <MessageSquare className="w-12 h-12 text-muted-foreground opacity-30 mb-3" />
                      <p className="text-white font-medium">No conversations yet</p>
                      <p className="text-sm text-muted-foreground mt-1">
                        Conversations will appear here when a booking is confirmed.
                      </p>
                    </div>
                  )}
                />
              </div>
              <div className="flex-1 min-w-0">
                <Channel>
                  <Window>
                    <ChannelHeader />
                    <MessageList />
                    <MessageInput />
                  </Window>
                  <Thread />
                </Channel>
              </div>
            </div>
          </Chat>
        </div>
      </div>
    </AppLayout>
  );
}
